import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { helpdeskSettingsApi } from '../../../api/services/helpdesk-settings.service';
import type { TicketTopic } from '../../../types/helpdeskSettingsInterfaces';
import { modalCls } from '@shared/ui/form-modal';

/*
 * Справочник «Тип проблемы» (тематика обращения). Вместе с полем
 * «Продукт» даёт статистику Клиент — Продукт — Проблема (отчёт
 * «Продукты»). Темы группируются (например «Монтаж», «ПО»);
 * группа — свободный текст с подсказкой из уже существующих.
 */

const errText = (e: any): string => {
  const d = e?.response?.data;
  if (!d) return 'Не удалось сохранить.';
  if (typeof d.detail === 'string') return d.detail;
  const first = Object.values(d)[0];
  return Array.isArray(first) ? String(first[0]) : String(first ?? 'Ошибка');
};

interface TopicModalProps {
  topic?: TicketTopic | null;
  groups: string[];
  onClose: () => void;
  onSave: (data: Partial<TicketTopic>) => Promise<void>;
  onDelete?: (id: number) => Promise<void>;
}

const TopicModal: React.FC<TopicModalProps> = ({ topic, groups, onClose, onSave, onDelete }) => {
  const [name, setName] = useState(topic?.name || '');
  const [group, setGroup] = useState(topic?.group || '');
  const [description, setDescription] = useState(topic?.description || '');
  const [sortOrder, setSortOrder] = useState<number>(topic?.sort_order ?? 0);
  const [isActive, setIsActive] = useState(topic?.is_active ?? true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setError('');
    try {
      await onSave({
        name: name.trim(),
        group: group.trim(),
        description: description.trim(),
        sort_order: Number.isFinite(sortOrder) ? sortOrder : 0,
        is_active: isActive,
      });
    } catch (err) {
      setError(errText(err));
    }
  };

  const handleDelete = async () => {
    if (!topic || !onDelete) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setError('');
    try {
      await onDelete(topic.id);
    } catch (err) {
      setError(errText(err));
      setConfirmDelete(false);
    }
  };

  const input =
    'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400';

  return (
    <div className={modalCls.overlay}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className={`${modalCls.panel} max-w-md`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <h2 className="text-lg font-semibold text-[#2d2c2c]">
            {topic ? 'Редактировать тип проблемы' : 'Добавить тип проблемы'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className={modalCls.body}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Название <span className="text-red-500">*</span>
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например: Не видит датчик"
              className={input}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Группа</label>
            <input
              value={group}
              list="hd-topic-groups"
              onChange={(e) => setGroup(e.target.value)}
              placeholder="Например: Монтаж, Программное обеспечение"
              className={input}
            />
            <datalist id="hd-topic-groups">
              {groups.map((g) => (
                <option key={g} value={g} />
              ))}
            </datalist>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Описание</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Когда выбирать этот тип (видно только сотрудникам)"
              className={`${input} resize-none`}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Порядок</label>
              <input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(parseInt(e.target.value, 10))}
                className={input}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Статус</label>
              <select
                value={isActive ? 'active' : 'inactive'}
                onChange={(e) => setIsActive(e.target.value === 'active')}
                className={`${input} bg-white`}
              >
                <option value="active">Активен</option>
                <option value="inactive">Неактивен</option>
              </select>
            </div>
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}

          <div className={`flex items-center justify-between ${modalCls.footerSticky}`}>
            {topic && onDelete ? (
              <button
                type="button"
                onClick={handleDelete}
                onBlur={() => setConfirmDelete(false)}
                className={`text-sm font-medium transition-colors ${
                  confirmDelete
                    ? 'text-white bg-[#e3282a] px-3 py-2 rounded-lg hover:bg-[#c82022]'
                    : 'text-gray-400 hover:text-[#e3282a]'
                }`}
              >
                {confirmDelete ? 'Точно удалить?' : 'Удалить'}
              </button>
            ) : (
              <span />
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Отмена
              </button>
              <button
                type="submit"
                disabled={!name.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] disabled:opacity-50"
              >
                {topic ? 'Сохранить' : 'Добавить'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Основной экран ── */
const TopicSettings: React.FC = () => {
  const [topics, setTopics] = useState<TicketTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TicketTopic | null>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await helpdeskSettingsApi.getTicketTopics();
      setTopics(data);
      setError('');
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(
    () => Array.from(new Set(topics.map((t) => t.group).filter(Boolean))),
    [topics],
  );

  // Список сгруппирован по `group` (бэк уже отдаёт в порядке group, sort_order).
  const grouped = useMemo(() => {
    const map = new Map<string, TicketTopic[]>();
    topics.forEach((t) => {
      const k = t.group || '';
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(t);
    });
    return Array.from(map.entries());
  }, [topics]);

  const handleSave = async (data: Partial<TicketTopic>) => {
    if (editing) {
      await helpdeskSettingsApi.updateTicketTopic(editing.id, data);
    } else {
      await helpdeskSettingsApi.createTicketTopic(data);
    }
    setModalOpen(false);
    setEditing(null);
    await load();
  };

  const handleDelete = async (id: number) => {
    await helpdeskSettingsApi.deleteTicketTopic(id);
    setModalOpen(false);
    setEditing(null);
    await load();
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-1/3"></div>
          <div className="h-16 bg-gray-100 rounded"></div>
          <div className="h-16 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          Тип проблемы указывается оператором в заявке; вместе с продуктом
          даёт статистику «Клиент — Продукт — Проблема» в отчётах
        </p>
        <button
          onClick={() => { setEditing(null); setModalOpen(true); }}
          className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] transition-colors whitespace-nowrap"
        >
          + Добавить тип
        </button>
      </div>

      {error && <p className="mb-3 text-xs text-red-500">{error}</p>}

      {topics.length > 0 ? (
        <div>
          {grouped.map(([group, list]) => (
            <div key={group || '__none'} className="mb-3">
              {group && (
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mt-2 mb-1">
                  {group}
                </p>
              )}
              {list.map((t) => (
                <div
                  key={t.id}
                  onClick={() => { setEditing(t); setModalOpen(true); }}
                  className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#2d2c2c]">{t.name}</p>
                    {t.description && (
                      <p className="text-xs text-gray-400 truncate">{t.description}</p>
                    )}
                  </div>
                  <span
                    className={`text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 ml-4 ${
                      t.is_active
                        ? 'bg-green-50 text-green-600 border border-green-200'
                        : 'bg-gray-50 text-gray-500 border border-gray-200'
                    }`}
                  >
                    {t.is_active ? 'Активен' : 'Неактивен'}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-400 text-center py-8">
          Типов проблем пока нет. Нажмите «Добавить тип», чтобы создать первый.
        </p>
      )}

      {modalOpen && (
        <TopicModal
          topic={editing}
          groups={groups}
          onClose={() => { setModalOpen(false); setEditing(null); }}
          onSave={handleSave}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default TopicSettings;
